import React from "react";

interface LoadingSpinnerProps {
  size?: "sm" | "md" | "lg";
  message?: string;
  fullScreen?: boolean;
  className?: string;
}

const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = "md",
  message,
  fullScreen = false,
  className = "",
}) => {
  const sizeClasses = {
    sm: "w-4 h-4",
    md: "w-8 h-8",
    lg: "w-12 h-12",
  };

  const containerClasses = fullScreen
    ? "fixed inset-0 bg-neutral-50/90 dark:bg-neutral-950/90 backdrop-blur-sm flex items-center justify-center z-50"
    : "flex items-center justify-center p-4";

  return (
    <div className={`${containerClasses} ${className} animate-fade-in`}>
      <div className="text-center">
        <div className={`${sizeClasses[size]} animate-spin mx-auto mb-2`}>
          <svg
            className="w-full h-full text-primary-600 dark:text-primary-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 2a10 10 0 0 1 10 10M12 22a10 10 0 0 1-10-10"
            />
          </svg>
        </div>
        {message && (
          <p className="text-sm text-neutral-600 dark:text-neutral-400 font-medium">{message}</p>
        )}
      </div>
    </div>
  );
};

const skeletonWidths = ["82%", "64%", "72%", "56%", "88%"];

interface LoadingCardProps {
  className?: string;
  rows?: number;
}

export const LoadingCard: React.FC<LoadingCardProps> = ({
  className = "",
  rows = 3,
}) => {
  return (
    <div className={`rounded-2xl border border-neutral-200/80 bg-white/80 p-6 shadow-[0_18px_60px_rgba(23,23,23,0.06)] dark:border-neutral-800 dark:bg-neutral-900/70 dark:shadow-none ${className}`}>
      <div className="animate-pulse">
        {/* Image skeleton */}
        <div className="mb-5 aspect-[4/3] rounded-2xl bg-neutral-200 dark:bg-neutral-800"></div>
        {/* Title skeleton */}
        <div className="h-5 w-3/4 rounded bg-neutral-200 dark:bg-neutral-800 mb-4"></div>
        {/* Content skeleton */}
        {Array.from({ length: rows }).map((_, index) => (
          <div
            key={index}
            className="h-3 rounded bg-neutral-200 dark:bg-neutral-800 mb-2"
            style={{ width: skeletonWidths[index % skeletonWidths.length] }}
          ></div>
        ))}
      </div>
    </div>
  );
};

interface LoadingTableProps {
  rows?: number;
  columns?: number;
  className?: string;
}

export const LoadingTable: React.FC<LoadingTableProps> = ({
  rows = 5,
  columns = 4,
  className = "",
}) => {
  return (
    <div className={`overflow-x-auto ${className}`}>
      <table className="min-w-full animate-pulse">
        <thead>
          <tr className="bg-neutral-50 dark:bg-neutral-900">
            {Array.from({ length: columns }).map((_, index) => (
              <th key={index} className="py-3 px-4">
                <div className="h-3 bg-neutral-200 dark:bg-neutral-800 rounded"></div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800">
          {Array.from({ length: rows }).map((_, rowIndex) => (
            <tr key={rowIndex}>
              {Array.from({ length: columns }).map((_, colIndex) => (
                <td key={colIndex} className="py-3 px-4">
                  <div
                    className="h-3 bg-neutral-200 dark:bg-neutral-800 rounded"
                    style={{
                      width: skeletonWidths[(rowIndex + colIndex) % skeletonWidths.length],
                    }}
                  ></div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default LoadingSpinner;
